import { useState, useEffect } from 'react'
import { AdminAlert, AdminComingSoon, isNotBuiltYet } from '../AdminShared'
import { Skeleton } from '../components/Skeleton'
import { adminGetUsers, adminGetUserById, adminSuspendUser, adminUnsuspendUser, adminAddCPT, adminReduceCPT, adminAddCisScore, adminSubtractCisScore, adminDeleteUser, adminAssignBadge } from '../../../services/admin'

export function UserManagement() {
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState('all')
  const [users, setUsers] = useState([])
  const [meta, setMeta] = useState({ total: 0, page: 1, limit: 10, totalPages: 1, nextPage: null, prevPage: null })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [tierFilter, setTierFilter] = useState('all')
  const [sortBy, setSortBy] = useState('none') // 'none' | 'cisPoints' | 'cptEarned'
  const [sortDir, setSortDir] = useState('desc') // 'desc' | 'asc'
  const [adjustModal, setAdjustModal] = useState(null) // { user, type: 'cpt'|'cis', action: 'add'|'reduce'|'subtract' }
  const [adjustAmount, setAdjustAmount] = useState('')
  const [adjustReason, setAdjustReason] = useState('')
  const [adjustLoading, setAdjustLoading] = useState(false)
  const [adjustError, setAdjustError] = useState(null)

  const openAdjust = (user, type, action) => {
    setAdjustModal({ user, type, action })
    setAdjustAmount('')
    setAdjustReason('')
    setAdjustError(null)
  }
  const closeAdjust = () => { setAdjustModal(null); setAdjustError(null) }

  const handleAdjustSubmit = async () => {
    const amt = Number(adjustAmount)
    if (!amt || amt <= 0) { setAdjustError('Enter a valid positive number'); return }
    if (adjustModal.type === 'cpt' && !adjustReason.trim()) { setAdjustError('Reason is required for CPT adjustments'); return }
    setAdjustLoading(true)
    setAdjustError(null)
    try {
      const { user, type, action } = adjustModal

      // GET /users (list) does not return profile.id, only user.id — and the
      // CPT/CIS backend endpoints require the real profile.id (userStat is
      // keyed on profileId, not userId). Resolve it via GET /users/:id,
      // which does eager-load the profile relation, right before submitting.
      const full = await adminGetUserById(user.id)
      const profileId = full?.profile?.id ?? full?.data?.profile?.id
      if (!profileId) {
        setAdjustError('Could not resolve this user\'s profile ID — they may not have a profile yet.')
        setAdjustLoading(false)
        return
      }

      if (type === 'cpt') {
        if (action === 'add') await adminAddCPT(profileId, amt, adjustReason)
        else await adminReduceCPT(profileId, amt, adjustReason)
      } else {
        if (action === 'add') await adminAddCisScore(profileId, amt)
        else await adminSubtractCisScore(profileId, amt)
      }
      closeAdjust()
      fetchUsers()
    } catch (err) {
      setAdjustError(err.message ?? 'Adjustment failed')
    } finally {
      setAdjustLoading(false)
    }
  }

  // Verified badge modal — gift (or remove) gold/blue/grey
  const [badgeModal, setBadgeModal] = useState(null) // { user }
  const [badgeValue, setBadgeValue] = useState('none')
  const [badgeLoading, setBadgeLoading] = useState(false)
  const [badgeError, setBadgeError] = useState(null)

  const openBadge = (user) => {
    setBadgeModal({ user })
    setBadgeValue(user.verifiedBadge || 'none')
    setBadgeError(null)
  }
  const closeBadge = () => { setBadgeModal(null); setBadgeError(null) }

  const handleBadgeSubmit = async () => {
    setBadgeLoading(true)
    setBadgeError(null)
    try {
      const { user } = badgeModal
      // Same resolution as CPT/CIS above — GET /users (list) doesn't include
      // profile.id, so fetch the full record right before submitting.
      const full = await adminGetUserById(user.id)
      const profileId = full?.profile?.id ?? full?.data?.profile?.id
      if (!profileId) {
        setBadgeError('Could not resolve this user\'s profile ID — they may not have a profile yet.')
        setBadgeLoading(false)
        return
      }
      await adminAssignBadge(profileId, badgeValue)
      closeBadge()
      fetchUsers()
    } catch (err) {
      setBadgeError(err.message ?? 'Failed to update badge')
    } finally {
      setBadgeLoading(false)
    }
  }

  // Delete user modal
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [deleteConfirmText, setDeleteConfirmText] = useState('')
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState(null)

  const openDelete = (user) => { setDeleteTarget(user); setDeleteConfirmText(''); setDeleteError(null) }
  const closeDelete = () => { setDeleteTarget(null); setDeleteError(null) }

  const handleDeleteConfirm = async () => {
    if (deleteConfirmText.trim() !== deleteTarget.email) {
      setDeleteError('Email does not match')
      return
    }
    setDeleting(true)
    setDeleteError(null)
    try {
      await adminDeleteUser(deleteTarget.id)
      closeDelete()
      fetchUsers()
    } catch (err) {
      setDeleteError(err.message ?? 'Failed to delete user')
    } finally {
      setDeleting(false)
    }
  }

  const fetchUsers = (s = search, f = filter, p = meta.page) => {
    setLoading(true)
    setError(null)
    adminGetUsers({ search: s, filter: f, page: p })
      .then(res => {
        const list = Array.isArray(res?.data) ? res.data : []
        setUsers(list)
        setMeta(res?.meta ?? { total: list.length, page: p, limit: 10, totalPages: 1, nextPage: null, prevPage: null })
      })
      .catch(err => setError(err))
      .finally(() => setLoading(false))
  }

  useEffect(() => { fetchUsers() }, [])

  const handleSearch = (val) => { setSearch(val); fetchUsers(val, filter, 1) }
  const handleFilter = (val) => { setFilter(val); fetchUsers(search, val, 1) }
  const handlePrev   = () => { if (meta.prevPage) fetchUsers(search, filter, meta.prevPage) }
  const handleNext   = () => { if (meta.nextPage) fetchUsers(search, filter, meta.nextPage) }

  const handleToggleSuspend = async (u) => {
    try {
      if (u.status === 'suspended') await adminUnsuspendUser(u.id)
      else await adminSuspendUser(u.id)
      fetchUsers()
    } catch (err) {
      setError(err)
    }
  }

  // Tier filter + CIS/CPT sort, applied client-side to the currently loaded page.
  // Desktop table and mobile cards both read from this so they stay in sync.
  const displayUsers = users
    .filter(u => tierFilter === 'all' || (u.tier ?? 'bronze') === tierFilter)
    .slice()
    .sort((a, b) => {
      if (sortBy === 'none') return 0
      const av = a[sortBy] ?? 0
      const bv = b[sortBy] ?? 0
      return sortDir === 'desc' ? bv - av : av - bv
    })

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDir(d => d === 'desc' ? 'asc' : 'desc')
    } else {
      setSortBy(field)
      setSortDir('desc')
    }
  }

  const statusColors = {
    active:    'bg-green-100 text-green-800 border-green-700',
    suspended: 'bg-error-container text-on-error-container border-error',
    pending:   'bg-primary-fixed text-on-primary-fixed border-primary',
  }
  const tierColors = {
    bronze:  'bg-surface-container text-on-surface-variant border-on-background/20',
    silver:  'bg-primary-container/20 text-on-primary-container border-primary-container/60',
    gold:    'bg-primary-container text-on-primary-container border-primary-container',
    diamond: 'bg-on-background text-surface border-on-background',
  }

  if (error && isNotBuiltYet(error) && users.length === 0) {
    return <AdminComingSoon label="User management" />
  }

  return (
    <>
      {adjustModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-on-background/60 p-4" onClick={e => { if (e.target === e.currentTarget) closeAdjust() }}>
          <div className="bg-surface border-4 border-on-background w-full max-w-sm flex flex-col gap-5 p-6"
            style={{ boxShadow: '6px 6px 0px 0px var(--neo-border-color)' }}>
            <div className="flex items-center justify-between">
              <h2 className="font-extrabold text-[14px] uppercase tracking-widest">
                {adjustModal.action === 'add' ? '+ Add' : '− Reduce'} {adjustModal.type.toUpperCase()}
              </h2>
              <button onClick={closeAdjust} className="p-1 hover:bg-surface-container transition-colors">
                <span className="material-symbols-outlined text-[20px]">close</span>
              </button>
            </div>
            <p className="text-[12px] text-on-surface-variant">
              User: <span className="font-bold text-on-surface">{adjustModal.user.displayName || adjustModal.user.username || adjustModal.user.email}</span>
            </p>
            {adjustError && <AdminAlert type="error" message={adjustError} />}
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1">
                <label className="font-bold text-[10px] uppercase tracking-widest text-on-surface-variant">Amount</label>
                <input
                  type="number" min="1" placeholder="e.g. 100"
                  value={adjustAmount} onChange={e => setAdjustAmount(e.target.value)}
                  className="bg-surface border-2 border-on-background px-3 py-2 font-mono text-[14px] focus:outline-none focus:border-primary-container"
                />
              </div>
              {adjustModal.type === 'cpt' && (
                <div className="flex flex-col gap-1">
                  <label className="font-bold text-[10px] uppercase tracking-widest text-on-surface-variant">Reason <span className="text-error">*</span></label>
                  <input
                    type="text" placeholder="e.g. Manual adjustment for testing"
                    value={adjustReason} onChange={e => setAdjustReason(e.target.value)}
                    className="bg-surface border-2 border-on-background px-3 py-2 text-[13px] focus:outline-none focus:border-primary-container"
                  />
                </div>
              )}
            </div>
            <div className="flex gap-3">
              <button onClick={closeAdjust}
                className="flex-1 py-2.5 border-2 border-on-background font-bold text-[11px] uppercase tracking-widest hover:bg-surface-container transition-colors">
                Cancel
              </button>
              <button onClick={handleAdjustSubmit} disabled={adjustLoading}
                className="flex-1 py-2.5 bg-on-background text-surface font-bold text-[11px] uppercase tracking-widest hover:opacity-90 transition-opacity disabled:opacity-50">
                {adjustLoading ? 'Saving…' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
      {badgeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-on-background/60 p-4" onClick={e => { if (e.target === e.currentTarget) closeBadge() }}>
          <div className="bg-surface border-4 border-on-background w-full max-w-sm flex flex-col gap-5 p-6"
            style={{ boxShadow: '6px 6px 0px 0px var(--neo-border-color)' }}>
            <div className="flex items-center justify-between">
              <h2 className="font-extrabold text-[14px] uppercase tracking-widest">Verified Badge</h2>
              <button onClick={closeBadge} className="p-1 hover:bg-surface-container transition-colors">
                <span className="material-symbols-outlined text-[20px]">close</span>
              </button>
            </div>
            <p className="text-[12px] text-on-surface-variant">
              User: <span className="font-bold text-on-surface">{badgeModal.user.displayName || badgeModal.user.username || badgeModal.user.email}</span>
            </p>
            {badgeError && <AdminAlert type="error" message={badgeError} />}
            <div className="flex flex-col gap-1">
              <label className="font-bold text-[10px] uppercase tracking-widest text-on-surface-variant">Badge</label>
              <select
                value={badgeValue} onChange={e => setBadgeValue(e.target.value)}
                className="bg-surface border-2 border-on-background px-3 py-2 font-bold text-[13px] focus:outline-none focus:border-primary-container"
              >
                <option value="none">None (remove badge)</option>
                <option value="gold">Gold</option>
                <option value="blue">Blue</option>
                <option value="grey">Grey</option>
              </select>
            </div>
            <div className="flex gap-3">
              <button onClick={closeBadge}
                className="flex-1 py-2.5 border-2 border-on-background font-bold text-[11px] uppercase tracking-widest hover:bg-surface-container transition-colors">
                Cancel
              </button>
              <button onClick={handleBadgeSubmit} disabled={badgeLoading}
                className="flex-1 py-2.5 bg-on-background text-surface font-bold text-[11px] uppercase tracking-widest hover:opacity-90 transition-opacity disabled:opacity-50">
                {badgeLoading ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-on-background/60 p-4" onClick={e => { if (e.target === e.currentTarget) closeDelete() }}>
          <div className="bg-surface border-4 border-error w-full max-w-sm flex flex-col gap-5 p-6"
            style={{ boxShadow: '6px 6px 0px 0px var(--neo-border-color)' }}>
            <div className="flex items-center justify-between">
              <h2 className="font-extrabold text-[14px] uppercase tracking-widest text-error">Delete User</h2>
              <button onClick={closeDelete} className="p-1 hover:bg-surface-container transition-colors">
                <span className="material-symbols-outlined text-[20px]">close</span>
              </button>
            </div>
            <p className="text-[12px] text-on-surface-variant">
              This will permanently delete <span className="font-bold text-on-surface">{deleteTarget.email}</span>. This cannot be undone.
              Type their email to confirm.
            </p>
            {deleteError && <AdminAlert type="error" message={deleteError} />}
            <input
              type="text" placeholder={deleteTarget.email}
              value={deleteConfirmText} onChange={e => setDeleteConfirmText(e.target.value)}
              className="bg-surface border-2 border-on-background px-3 py-2 font-mono text-[13px] focus:outline-none focus:border-error"
            />
            <div className="flex gap-3">
              <button onClick={closeDelete}
                className="flex-1 py-2.5 border-2 border-on-background font-bold text-[11px] uppercase tracking-widest hover:bg-surface-container transition-colors">
                Cancel
              </button>
              <button onClick={handleDeleteConfirm} disabled={deleting || deleteConfirmText.trim() !== deleteTarget.email}
                className="flex-1 py-2.5 bg-error text-on-error font-bold text-[11px] uppercase tracking-widest hover:opacity-90 transition-opacity disabled:opacity-50">
                {deleting ? 'Deleting…' : 'Delete User'}
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="flex flex-col gap-6">
        {error && !isNotBuiltYet(error) && <AdminAlert type="error" message={error.message} />}

      {/* Controls */}
      <div className="flex flex-col gap-3">
        <div className="relative w-full">
          <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-[18px] text-on-surface-variant">search</span>
          <input
            type="text" placeholder="Search users…"
            value={search} onChange={e => handleSearch(e.target.value)}
            className="w-full bg-surface border-4 border-on-background pl-11 pr-4 py-3 font-body-md focus:ring-0 focus:outline-none focus:border-primary-container transition-all"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {['all', 'active', 'suspended'].map(f => (
            <button key={f} onClick={() => handleFilter(f)}
              className={`flex-1 min-w-[100px] px-4 py-3 border-4 border-on-background font-bold text-[11px] uppercase tracking-widest transition-all capitalize
                ${filter === f ? 'bg-on-background text-surface' : 'bg-surface text-on-surface hover:bg-surface-container'}`}
              style={filter === f ? { boxShadow: '3px 3px 0px 0px #d4af37' } : { boxShadow: '3px 3px 0px 0px var(--neo-border-color)' }}>
              {f}
            </button>
          ))}
        </div>

        {/* Tier filter + CIS/CPT sort — client-side, applies to the loaded page */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex flex-wrap gap-2 flex-1">
            {['all', 'bronze', 'silver', 'gold', 'diamond'].map(t => (
              <button key={t} onClick={() => setTierFilter(t)}
                className={`flex-1 min-w-[80px] px-3 py-2 border-2 border-on-background font-bold text-[10px] uppercase tracking-widest transition-all capitalize
                  ${tierFilter === t ? 'bg-primary-container text-on-primary-container' : 'bg-surface text-on-surface hover:bg-surface-container'}`}>
                {t}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            {[{ field: 'cisPoints', label: 'CIS' }, { field: 'cptEarned', label: 'CPT' }].map(({ field, label }) => (
              <button key={field} onClick={() => handleSort(field)}
                className={`flex-1 sm:flex-none flex items-center justify-center gap-1 px-3 py-2 border-2 border-on-background font-bold text-[10px] uppercase tracking-widest transition-all
                  ${sortBy === field ? 'bg-on-background text-surface' : 'bg-surface text-on-surface hover:bg-surface-container'}`}>
                {label}
                <span className="material-symbols-outlined text-[14px]">
                  {sortBy === field ? (sortDir === 'desc' ? 'arrow_downward' : 'arrow_upward') : 'unfold_more'}
                </span>
              </button>
            ))}
            {sortBy !== 'none' && (
              <button onClick={() => setSortBy('none')}
                title="Clear sort"
                className="px-3 py-2 border-2 border-on-background font-bold text-[10px] uppercase tracking-widest text-on-surface-variant hover:bg-surface-container transition-all">
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Mobile cards (≤1023px) */}
      <div className="lg:hidden flex flex-col gap-4">
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="border-4 border-on-background bg-surface rounded-lg p-4 flex flex-col gap-3"
              style={{ boxShadow: '5px 5px 0px 0px var(--neo-border-color)' }}>
              <Skeleton className="h-5 w-2/3 rounded" />
              <Skeleton className="h-4 w-1/3 rounded" />
              <Skeleton className="h-4 w-full rounded" />
              <Skeleton className="h-9 w-full rounded" />
            </div>
          ))
        ) : displayUsers.length === 0 ? (
          <div className="border-4 border-on-background bg-surface rounded-lg p-8 text-center"
            style={{ boxShadow: '5px 5px 0px 0px var(--neo-border-color)' }}>
            <p className="text-on-surface-variant font-bold text-[12px] uppercase tracking-widest">No users found</p>
          </div>
        ) : (
          displayUsers.map(u => (
            <div key={u.id} className="border-4 border-on-background bg-surface rounded-lg p-4 flex flex-col gap-3"
              style={{ boxShadow: '5px 5px 0px 0px var(--neo-border-color)' }}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex flex-col gap-0.5 min-w-0">
                  <h3 className="font-bold text-[15px] leading-tight break-words">{u.displayName || '—'}</h3>
                  <p className="text-[12px] text-on-surface-variant font-mono break-words">{u.username ? `@${u.username}` : '—'}</p>
                </div>
                <span className={`shrink-0 font-bold text-[10px] uppercase tracking-widest px-2 py-1 border-2 rounded ${statusColors[u.status] ?? ''}`}>{u.status}</span>
              </div>

              <div className="flex flex-col gap-1 border-t-2 border-on-background/10 pt-3 text-[12px]">
                <div className="flex justify-between gap-3">
                  <span className="text-on-surface-variant font-bold uppercase tracking-wide text-[10px] shrink-0">Email</span>
                  <span className="font-mono text-right break-all">{u.email}</span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-on-surface-variant font-bold uppercase tracking-wide text-[10px] shrink-0">Role</span>
                  <span className="font-bold uppercase text-right">{u.role ?? '—'}</span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-on-surface-variant font-bold uppercase tracking-wide text-[10px] shrink-0">Verified</span>
                  <span className="font-bold text-right">{u.isVerified ? 'Yes' : 'No'}</span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-on-surface-variant font-bold uppercase tracking-wide text-[10px] shrink-0">Joined</span>
                  <span className="font-mono text-right">{u.joined ? new Date(u.joined).toLocaleDateString() : '—'}</span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-on-surface-variant font-bold uppercase tracking-wide text-[10px] shrink-0">Last Login</span>
                  <span className="font-mono text-right">{u.lastLogin ? new Date(u.lastLogin).toLocaleDateString() : '—'}</span>
                </div>
                <div className="flex justify-between items-center gap-3">
                  <span className="text-on-surface-variant font-bold uppercase tracking-wide text-[10px] shrink-0">Tier</span>
                  <span className={`font-bold text-[10px] uppercase tracking-widest px-2 py-0.5 border-2 rounded ${tierColors[u.tier] ?? tierColors.bronze}`}>{u.tier ?? 'bronze'}</span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-on-surface-variant font-bold uppercase tracking-wide text-[10px] shrink-0">CIS Points</span>
                  <span className="font-mono font-bold text-right">{u.cisPoints ?? 0}</span>
                </div>
                <div className="flex justify-between items-center gap-3">
                  <span className="text-on-surface-variant font-bold uppercase tracking-wide text-[10px] shrink-0">Badge</span>
                  <span className="font-bold text-[10px] uppercase tracking-widest text-right">{u.verifiedBadge && u.verifiedBadge !== 'none' ? u.verifiedBadge : '—'}</span>
                </div>
              </div>

              <button
                onClick={() => handleToggleSuspend(u)}
                className="w-full min-h-[44px] mt-1 flex items-center justify-center gap-2 border-2 border-on-background font-bold text-[11px] uppercase tracking-widest active:bg-error active:text-on-error transition-colors rounded"
              >
                <span className="material-symbols-outlined text-[16px]">{u.status === 'suspended' ? 'lock_open' : 'block'}</span>
                {u.status === 'suspended' ? 'Unsuspend' : 'Suspend'}
              </button>
              <button
                onClick={() => openBadge(u)}
                className="w-full min-h-[44px] mt-1 flex items-center justify-center gap-2 border-2 border-on-background font-bold text-[11px] uppercase tracking-widest hover:bg-primary-container hover:text-on-primary-container transition-colors rounded"
              >
                <span className="material-symbols-outlined text-[16px]">verified</span>
                Badge
              </button>
              <div className="grid grid-cols-2 gap-2 mt-1">
                {[
                  { label: '+ CPT', type: 'cpt', action: 'add' },
                  { label: '− CPT', type: 'cpt', action: 'reduce' },
                  { label: '+ CIS', type: 'cis', action: 'add' },
                  { label: '− CIS', type: 'cis', action: 'subtract' },
                ].map(({ label, type, action }) => (
                  <button key={label} onClick={() => openAdjust(u, type, action)}
                    className="py-2 border-2 border-on-background font-bold text-[10px] uppercase tracking-widest hover:bg-primary-container hover:text-on-primary-container transition-colors rounded">
                    {label}
                  </button>
                ))}
              </div>
              <button
                onClick={() => openDelete(u)}
                className="w-full min-h-[44px] mt-1 flex items-center justify-center gap-2 border-2 border-error text-error font-bold text-[11px] uppercase tracking-widest active:bg-error active:text-on-error transition-colors rounded"
              >
                <span className="material-symbols-outlined text-[16px]">delete</span>
                Delete User
              </button>
            </div>
          ))
        )}

        {/* Mobile pagination */}
        <div className="border-4 border-on-background bg-surface-container-low rounded-lg p-4 flex flex-col items-center gap-3"
          style={{ boxShadow: '5px 5px 0px 0px var(--neo-border-color)' }}>
          <p className="font-bold text-[10px] uppercase tracking-widest text-on-surface-variant text-center">
            {loading ? 'Loading…' : `Showing ${displayUsers.length} of ${meta.total} users`}
          </p>
          <div className="flex items-center gap-3">
            <button onClick={handlePrev} disabled={!meta.prevPage || loading}
              className="min-h-[44px] px-5 flex items-center gap-1 border-2 border-on-background font-bold text-[11px] uppercase tracking-widest active:bg-on-background active:text-surface transition-colors disabled:opacity-40 rounded">
              <span className="material-symbols-outlined text-[16px]">arrow_back</span> Previous
            </button>
            <button onClick={handleNext} disabled={!meta.nextPage || loading}
              className="min-h-[44px] px-5 flex items-center gap-1 border-2 border-on-background font-bold text-[11px] uppercase tracking-widest active:bg-on-background active:text-surface transition-colors disabled:opacity-40 rounded">
              Next <span className="material-symbols-outlined text-[16px]">arrow_forward</span>
            </button>
          </div>
        </div>
      </div>

      {/* Desktop table (≥1024px) */}
      <div className="hidden lg:block border-4 border-on-background overflow-x-auto" style={{ boxShadow: '6px 6px 0px 0px var(--neo-border-color)' }}>
        <table className="w-full">
          <thead>
            <tr className="bg-on-background text-surface border-b-4 border-on-background">
              {['Email', 'Username', 'Display Name', 'Role', 'Verified', 'Badge', 'Date Signup', 'Last Login', 'Tier', 'CIS Points', 'CPT Earned', 'Status', 'Actions'].map(h => (
                <th key={h} className="px-6 py-4 text-left font-bold text-[10px] uppercase tracking-[0.14em] whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i} className="border-b-2 border-on-background/10">
                  {Array.from({ length: 13 }).map((_, j) => (
                    <td key={j} className="px-6 py-4"><Skeleton className="h-5 w-24" /></td>
                  ))}
                </tr>
              ))
            ) : displayUsers.length === 0 ? (
              <tr>
                <td colSpan={13} className="px-6 py-12 text-center text-on-surface-variant font-bold text-[12px] uppercase tracking-widest">
                  No users found
                </td>
              </tr>
            ) : (
              displayUsers.map((u, i) => (
                <tr key={u.id} className={`border-b-2 border-on-background/10 hover:bg-primary-fixed/40 transition-colors ${i % 2 === 0 ? 'bg-surface' : 'bg-surface-container-low'}`}>
                  <td className="px-6 py-4 text-[12px] font-mono">{u.email}</td>
                  <td className="px-6 py-4 text-[13px]">{u.username ? `@${u.username}` : '—'}</td>
                  <td className="px-6 py-4 font-bold text-[13px]">{u.displayName || '—'}</td>
                  <td className="px-6 py-4 text-[12px] uppercase font-bold text-on-surface-variant">{u.role ?? '—'}</td>
                  <td className="px-6 py-4">
                    <span className={`font-bold text-[10px] uppercase tracking-widest px-2 py-1 border-2 ${u.isVerified ? 'border-primary-container text-on-primary-container bg-primary-container/20' : 'border-on-background/20 text-on-surface-variant'}`}>
                      {u.isVerified ? 'Verified' : 'Unverified'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`font-bold text-[10px] uppercase tracking-widest px-2 py-1 border-2 ${u.verifiedBadge && u.verifiedBadge !== 'none' ? 'border-primary-container text-on-primary-container bg-primary-container/20' : 'border-on-background/20 text-on-surface-variant'}`}>
                      {u.verifiedBadge && u.verifiedBadge !== 'none' ? u.verifiedBadge : '—'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-[12px] text-on-surface-variant font-mono">
                    {u.joined ? new Date(u.joined).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-6 py-4 text-[12px] text-on-surface-variant font-mono">
                    {u.lastLogin ? new Date(u.lastLogin).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`font-bold text-[10px] uppercase tracking-widest px-2 py-1 border-2 ${tierColors[u.tier] ?? tierColors.bronze}`}>{u.tier ?? 'bronze'}</span>
                  </td>
                  <td className="px-6 py-4 font-mono font-bold text-[13px]">{u.cisPoints ?? 0}</td>
                  <td className="px-6 py-4 font-mono font-bold text-[13px]">{u.cptEarned ?? 0}</td>
                  <td className="px-6 py-4">
                    <span className={`font-bold text-[10px] uppercase tracking-widest px-2 py-1 border-2 ${statusColors[u.status] ?? ''}`}>{u.status}</span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleToggleSuspend(u)}
                        className="p-1.5 border-2 border-on-background hover:bg-error hover:text-on-error transition-colors"
                        title={u.status === 'suspended' ? 'Unsuspend' : 'Suspend'}
                      >
                        <span className="material-symbols-outlined text-[16px]">{u.status === 'suspended' ? 'lock_open' : 'block'}</span>
                      </button>
                      <button onClick={() => openAdjust(u, 'cpt', 'add')} title="Add CPT"
                        className="p-1.5 border-2 border-on-background hover:bg-primary-container hover:text-on-primary-container transition-colors font-bold text-[10px]">+CPT</button>
                      <button onClick={() => openAdjust(u, 'cpt', 'reduce')} title="Reduce CPT"
                        className="p-1.5 border-2 border-on-background hover:bg-error-container hover:text-on-error-container transition-colors font-bold text-[10px]">−CPT</button>
                      <button onClick={() => openAdjust(u, 'cis', 'add')} title="Add CIS"
                        className="p-1.5 border-2 border-on-background hover:bg-primary-container hover:text-on-primary-container transition-colors font-bold text-[10px]">+CIS</button>
                      <button onClick={() => openAdjust(u, 'cis', 'subtract')} title="Subtract CIS"
                        className="p-1.5 border-2 border-on-background hover:bg-error-container hover:text-on-error-container transition-colors font-bold text-[10px]">−CIS</button>
                      <button onClick={() => openBadge(u)} title="Gift/remove verified badge"
                        className="p-1.5 border-2 border-on-background hover:bg-primary-container hover:text-on-primary-container transition-colors">
                        <span className="material-symbols-outlined text-[16px]">verified</span>
                      </button>
                      <button onClick={() => openDelete(u)} title="Delete User"
                        className="p-1.5 border-2 border-error text-error hover:bg-error hover:text-on-error transition-colors">
                        <span className="material-symbols-outlined text-[16px]">delete</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        <div className="px-6 py-4 border-t-4 border-on-background bg-surface-container-low flex items-center justify-between">
          <p className="font-bold text-[10px] uppercase tracking-widest text-on-surface-variant">
            {loading ? 'Loading…' : `Showing ${displayUsers.length} of ${meta.total} users — page ${meta.page} of ${meta.totalPages || 1}`}
          </p>
          <div className="flex gap-2">
            <button onClick={handlePrev} disabled={!meta.prevPage || loading}
              className="px-4 py-2 border-2 border-on-background font-bold text-[11px] uppercase tracking-widest hover:bg-on-background hover:text-surface transition-colors disabled:opacity-40">Prev</button>
            <button onClick={handleNext} disabled={!meta.nextPage || loading}
              className="px-4 py-2 border-2 border-on-background font-bold text-[11px] uppercase tracking-widest hover:bg-on-background hover:text-surface transition-colors disabled:opacity-40">Next</button>
          </div>
        </div>
      </div>
    </div>
    </>
  )
}